<script lang="ts" setup>
import type { FormKitFrameworkContext } from "@formkit/core";
import { computed, ref, watch } from "vue";

const { context } = defineProps<{
  context: FormKitFrameworkContext;
}>();

// FormKit types `_value` as `unknown`; for this input it is always integer cents.
type CentsValue = string | number | null | undefined;

const cents = computed(() => context._value);

// The node value is integer cents (as a string); the field shows a decimal.
const centsToDisplay = (value: CentsValue): string => {
  const number = Number(value);

  return value === "" || value == null || isNaN(number)
    ? ""
    : (number / 100).toFixed(2);
};

const displayToCents = (display: string): string =>
  display === "" || isNaN(Number(display))
    ? ""
    : Math.round(Number(display) * 100).toString();

// Source of truth while typing; never reformatted mid-edit or it fights every keystroke.
const displayValue = ref(centsToDisplay(cents.value as CentsValue));

// Resync only on external changes (form reset / programmatic set), never on the user's typing —
// which always leaves _value equal to the cents the current display already represents.
watch(cents, (value) => {
  if (!context.applyAutoFormat) return;

  const incoming = value == null || value === "" ? "" : String(value);
  if (incoming !== displayToCents(displayValue.value)) {
    displayValue.value = centsToDisplay(value as CentsValue);
  }
});

const handleInput = (e: Event) => {
  const raw = (e.target as HTMLInputElement).value
    .replace(",", ".")
    .replace(/[^\d.]/g, "");
  const [whole = "", ...fractions] = raw.split(".");
  // single separator, truncated to hundredths (no rounding up).
  const display = fractions.length
    ? `${whole}.${fractions.join("").slice(0, 2)}`
    : whole;

  displayValue.value = display;
  context.node.input(displayToCents(display));
};

const handleBlur = () => {
  if (!context.applyAutoFormat || displayValue.value === "") return;

  const number = Number(displayValue.value);
  if (!isNaN(number)) displayValue.value = number.toFixed(2);
};
</script>

<template>
  <div class="flex justify-start items-center w-full">
    <span v-if="context.currencySymbol">
      {{ context.currencySymbol }}
    </span>
    <input
      :class="context.classes.input"
      :disabled="!!context.disabled"
      :value="displayValue"
      inputmode="decimal"
      placeholder="0.00"
      type="text"
      v-bind="context.attrs"
      @blur="handleBlur"
      @input="handleInput"
    />
    <span
      v-if="context.percentage !== undefined"
      class="text-gray-400"
    >
      {{ `${(context.percentage as Number).toFixed(2)}%` }}
    </span>
  </div>
</template>
